import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  shorthands,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import type { EmailVerification } from '@foundershub/api-client';
import {
  useEmailTokenValidationMutation,
  useEmailVerifyMutation,
  useUpdateUserMutation,
} from '@foundershub/api-client';
import { InputField, MessageBar } from '@foundershub/components';
import { ResendButton } from '../components/ResendButton';
import { getErrorMessages, getFieldError, getValidationState } from '../modules/errors';
import { useUserRequired } from '../store';

interface Props {
  onDismiss: () => void;
  verification: EmailVerification;
}

export function VerifyEmail({ onDismiss, verification }: Props) {
  const user = useUserRequired();
  const history = useHistory();
  const intl = useIntl();
  const classes = useStyles();

  const [
    validateToken,
    { data: validateData, isLoading: isValidateLoading, isError: isValidateError, error: validateError },
  ] = useEmailTokenValidationMutation();
  const [updateEmailVerification, { isLoading: isUpdateLoading, isError: isUpdateError, error: updateError }] =
    useEmailVerifyMutation();
  const [updateUserDetail, { isLoading: isUserLoading, isError: isUserError, error: userError, isSuccess }] =
    useUpdateUserMutation();
  const params = React.useMemo(() => new URLSearchParams(history.location.search), [history.location.search]);

  const [isResendError, setResendError] = React.useState(false);

  async function handleValidate(event: React.FormEvent) {
    event.preventDefault();
    const token = ((event.target as HTMLFormElement).elements.namedItem('token') as HTMLInputElement).value || '';

    validateToken({
      email: verification.email,
      token,
      id: verification.id,
    });
  }

  React.useEffect(() => {
    if (verification && validateData?.is_verified) {
      updateUserDetail({ contact_email: verification.email });
    }
  }, [validateData, verification]);

  async function handleCancel() {
    await updateEmailVerification({ id: verification.id, email: verification.email, cancelled_by_user: true });
  }

  const isLoading = isValidateLoading || isUpdateLoading || isUserLoading;
  const isError = isValidateError || isUpdateError || userError || isResendError;
  const error = isUserError ? userError : isUpdateError ? updateError : validateError;

  return (
    <Dialog open onOpenChange={(event, data) => !data.open && onDismiss()}>
      <DialogSurface aria-describedby={undefined}>
        <form onSubmit={handleValidate}>
          <DialogBody>
            <DialogTitle>{intl.formatMessage(title)}</DialogTitle>

            <DialogContent className={classes.formFields}>
              <Text>
                {intl.formatMessage(description, {
                  b: function Bold(str) {
                    // @ts-ignore formatjs/formatjs#3707
                    return (<Text className={classes.bold}>{str}</Text>) as string;
                  },
                  emailAddress: verification?.email || user.contact_email,
                })}
              </Text>

              <InputField
                defaultValue={params.get('token') || ''}
                label={intl.formatMessage(tokenLabel)}
                name="token"
                required
                validationMessage={getFieldError(error, 'token', intl)}
                validationState={getValidationState(error, 'token')}
              />

              {isError ? (
                <MessageBar type="danger">
                  {getErrorMessages(error, intl).map((value, i) => (
                    <Text key={i}>{value}</Text>
                  ))}
                </MessageBar>
              ) : null}

              {isSuccess ? (
                <MessageBar type="success">
                  <Text>
                    {intl.formatMessage(finalSuccessMessage, {
                      b: function Bold(str) {
                        // @ts-ignore formatjs/formatjs#3707
                        return (<Text style={styles.bold}>{str}</Text>) as string;
                      },
                      emailAddress: verification?.email || user.contact_email,
                    })}
                  </Text>
                </MessageBar>
              ) : null}
            </DialogContent>

            <DialogActions fluid={true} className={classes.actions}>
              <div>
                <ResendButton
                  verification={verification}
                  setShowError={setResendError}
                  buttonClass={classes.button}
                  transparent
                />
              </div>
              <div className={classes.endButtons}>
                {isLoading ? <Spinner size="small" /> : null}
                <DialogTrigger>
                  <Button appearance="secondary" onClick={handleCancel} className={classes.button}>
                    {intl.formatMessage(isSuccess ? doneLabel : cancelLabel)}
                  </Button>
                </DialogTrigger>
                {!isSuccess ? (
                  <Button disabled={isLoading} type="submit" appearance="primary" className={classes.button}>
                    {intl.formatMessage(submitLabel)}
                  </Button>
                ) : null}
              </div>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}

const title = defineMessage({
  defaultMessage: 'Verify email address',
  description: 'Modal title for change contact email form',
});
const description = defineMessage({
  defaultMessage:
    'We sent a verification email to <b>{emailAddress}</b>. Please input the token in the email below to verify your email. Please note that the token will expire within the next 10 minutes.',
  description: 'Success message for changing email address',
});
const finalSuccessMessage = defineMessage({
  defaultMessage: 'Your contact email address has been successfully updated to <b>{emailAddress}</b>.',
  description: 'Success message',
});
const tokenLabel = defineMessage({
  defaultMessage: 'Verification token',
  description: 'label for verification token field',
});
const submitLabel = defineMessage({
  defaultMessage: 'Submit',
  description: 'button label to verify new email address',
});
const cancelLabel = defineMessage({
  defaultMessage: 'Cancel',
  description: 'form submit cancel button',
});
const doneLabel = defineMessage({
  defaultMessage: 'Done',
  description: 'button label after form has been submitted successfully',
});

const useStyles = makeStyles({
  formFields: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  bold: {
    fontWeight: tokens.fontWeightSemibold,
  },
  actions: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
  endButtons: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
    '@media screen and (max-width: 480px)': {
      flexDirection: 'column',
    },
  },
  button: {
    width: '100%',
  },
});
